import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axios";
import EntityManagement from "../../components/formTemplates/EntityManagement";
import EntityTable from "../../components/templateComponents/EntityTable";
import useDropdownFetch from "../../components/apiFetch/useDropdownFetch";

const Campus = () => {
  const [formData, setFormData] = useState({
    campus_name: "",
    campus_address: "",
    csc_id: "",
    area_id: "",
    est_id: "",
    status_id: "",
  });

  const [selectedEstId, setSelectedEstId] = useState("");
  const [entities, setEntities] = useState([]);
  const [dropdownData, setDropdownData] = useState({
    csc_id: [],
    area_id: [],
    establishid: [],
    status_id: [],
  });

  const [editMode, setEditMode] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);

  const firstFormFieldRef = useRef(null);

  const endpoints = {
    csc: "/city/v0.1",
    area: "/area/v0.1",
    establishment: "/view_establishment/v1.1",
    status: "/view_status/v0.1",
    viewCampuses: "/view_campus/v1.1",
    registerCampus: "/registration_campus/v1.1",
    updateCampus: "/update_camp/v1.1",
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axiosInstance.post(endpoints.csc, {
          csc_id: 3,
          view_type: "view",
        });
        const formattedCityOptions = Object.entries(response.data.Data).map(
          ([value, label]) => ({ value, label })
        );
        setDropdownData((prev) => ({ ...prev, csc_id: formattedCityOptions }));
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    if (!formData.csc_id) {
      setDropdownData((prev) => ({ ...prev, area_id: [] }));
      return;
    }
    const fetchAreas = async () => {
      try {
        const response = await axiosInstance.post(endpoints.area, {
          csc_id: formData.csc_id,
          view_type: "view",
        });
        const formattedAreaOptions = Object.entries(response.data.Data).map(
          ([value, label]) => ({ value, label })
        );
        setDropdownData((prev) => ({ ...prev, area_id: formattedAreaOptions }));
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };
    fetchAreas();
  }, [formData.csc_id]);

  const { options: statusOptions } = useDropdownFetch(endpoints.status);
  useEffect(() => {
    if (statusOptions) {
      setDropdownData((prev) => ({ ...prev, status_id: statusOptions }));
    }
  }, [statusOptions]);

  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        const response = await axiosInstance.post(endpoints.establishment, {
          area_id: 1,
          view_type: "view",
        });
        const formattedEstablishments = Object.entries(response.data.Data).map(
          ([value, label]) => ({ value, label })
        );
        setDropdownData((prev) => ({
          ...prev,
          establishid: formattedEstablishments,
        }));
        if (formattedEstablishments.length > 0 && !selectedEstId) {
          setSelectedEstId(formattedEstablishments[0].value);
          setFormData((prev) => ({
            ...prev,
            est_id: formattedEstablishments[0].value,
          }));
        }
      } catch (error) {
        console.error("Error fetching establishments:", error);
      }
    };
    fetchEstablishments();
  }, [selectedEstId]);

  const fetchEntities = async () => {
    if (!selectedEstId) {
      setEntities([]);
      return;
    }
    try {
      const response = await axiosInstance.post(endpoints.viewCampuses, {
        view_type: "view all",
        est_id: selectedEstId,
      });
      setEntities(response.data.Data || []);
    } catch (error) {
      console.error("Error fetching campuses:", error);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, [selectedEstId]);

  // SYNC: Update selectedEstId when form's est_id changes
  useEffect(() => {
    if (formData.est_id && formData.est_id !== selectedEstId) {
      setSelectedEstId(formData.est_id);
    }
  }, [formData.est_id]);

  const handleUpdate = async (campusid, updatedData) => {
    try {
      const updatePayload = {
        campusid,
        campus_name: updatedData.campus_name,
        campus_address: updatedData.campus_address,
        est_id: updatedData.est_id,
        csc_id: updatedData.csc_id,
        area_id: updatedData.area_id,
        status_id: updatedData.status_id,
      };
      await axiosInstance.post(endpoints.updateCampus, updatePayload);
      await fetchEntities();
      resetForm();
    } catch (error) {
      console.error("Error updating campus:", error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formattedData = {
      ...formData,
      area_id: parseInt(formData.area_id, 10),
      csc_id: parseInt(formData.csc_id, 10),
      est_id: parseInt(formData.est_id, 10),
      status_id: parseInt(formData.status_id, 10),
    };

    try {
      if (editMode) {
        await handleUpdate(editingEntityId, formattedData);
      } else {
        const response = await axiosInstance.post(
          endpoints.registerCampus,
          formattedData,
          { headers: { "Content-Type": "application/json" } }
        );
        setEntities((prev) => [...prev, response.data.Data || response.data]);
        resetForm();
      }
    } catch (error) {
      console.error("Error submitting campus:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      campus_name: "",
      campus_address: "",
      csc_id: "",
      area_id: "",
      est_id: selectedEstId || "",
      status_id: "",
    });
    setEditMode(false);
    setEditingEntityId(null);
  };

  const handleEdit = async (campusid) => {
    try {
      const response = await axiosInstance.post(endpoints.viewCampuses, {
        view_type: "view",
        est_id: selectedEstId,
        campusid,
      });

      const campusData = response.data.Data;
      if (campusData) {
        setFormData({
          campus_name: campusData.campus_name || "",
          campus_address: campusData.campus_address || "",
          csc_id: campusData.csc_id || "",
          area_id: campusData.area_id || "",
          est_id: campusData.est_id || selectedEstId,
          status_id: campusData.status_id || "",
        });
        setEditMode(true);
        setEditingEntityId(campusid);
        setIsTableModalOpen(false);
        if (firstFormFieldRef.current) firstFormFieldRef.current.focus();
      }
    } catch (error) {
      console.error("Error fetching campus for edit:", error);
    }
  };



  const formFields = [
    { name: "campus_name", label: "Campus Name", type: "text", required: true },
    {
      name: "campus_address",
      label: "Campus Address",
      type: "text",
      required: true,
    },
    {
      name: "csc_id",
      label: "City",
      type: "select",
      dropdownKey: "csc_id",
      required: true,
      onChange: (e) =>
        setFormData((prev) => ({
          ...prev,
          csc_id: e.target.value,
          area_id: "",
        })),
    },
    {
      name: "area_id",
      label: "Area",
      type: "select",
      dropdownKey: "area_id",
      required: true,
    },
    {
      name: "est_id",
      label: "Establishment",
      type: "select",
      dropdownKey: "establishid",
      required: true,
    },
    {
      name: "status_id",
      label: "Status",
      type: "select",
      dropdownKey: "status_id",
      required: true,
    },
  ];

  const columns = [
    { name: "campus_name", label: "Campus Name" },
    { name: "campus_address", label: "Campus Address" },
    { name: "csc_name", label: "City" },
    { name: "area_name", label: "Area" },
    { name: "est_name", label: "Establishment" },
    { name: "status_name", label: "Status" },
  ];

  const handleViewTableClick = () => {
    setIsTableModalOpen(true);
  };

  return (
    <div className="campus">
      <EntityManagement
        entityName="Campus"
        formFields={formFields}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
        dropdownData={dropdownData}
        setDropdownData={setDropdownData}
        firstFormFieldRef={firstFormFieldRef}
        handleViewTableClick={handleViewTableClick}
        onEdit={handleEdit}
      />

      {isTableModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <button onClick={() => setIsTableModalOpen(false)}>Close</button>

            <div
              style={{
                marginBottom: "1.5rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "#f9fafb",
                padding: "1rem",
                borderRadius: "8px",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e5e7eb",
              }}
            >
              <label
                htmlFor="establishmentFilter"
                style={{
                  fontWeight: "600",
                  marginBottom: "0.6rem",
                  fontSize: "14px",
                  color: "#374151",
                }}
              >
                Filter by Establishment:
              </label>
              <select
                id="establishmentFilter"
                value={selectedEstId}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val !== selectedEstId) setSelectedEstId(val);
                  if (val !== formData.est_id)
                    setFormData((prev) => ({ ...prev, est_id: val }));
                }}
                style={{
                  padding: "10px 14px",
                  fontSize: "14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  width: "100%",
                  maxWidth: "200px",
                  backgroundColor: "#fff",
                  color: "#111827",
                  appearance: "none",
                  cursor: "pointer",
                }}
              >
                <option value="">All</option>
                {dropdownData.establishid.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <EntityTable
              data={entities.filter((item) => item && item.campusid != null)}
              columns={columns}
              onEdit={handleEdit}
              keyField="campusid"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Campus;
